import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, CreditCard, Lock, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpay = () =>
    new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = RAZORPAY_SCRIPT_SRC;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error("Failed to load Razorpay checkout"));
        document.body.appendChild(s);
    });

const FEATURES = [
    "Unlimited practice sessions",
    "Multi-session client engagements (up to 8 sessions per series)",
    "Full supervisor reviews with 8-category rubric",
    "Complete Learn library — 31 chapters across 6 methods",
    "Progress tracking, sparklines, and skill levelling",
    "Dynamic on-demand case generation",
];

export default function SubscribePage() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    useDocumentTitle("Subscribe");

    useEffect(() => {
        let cancelled = false;
        api.get("/payments/config")
            .then((r) => { if (!cancelled) setConfig(r.data); })
            .catch((e) => { if (!cancelled) setLoadError(e?.response?.data?.detail || "Failed to load plan"); });
        return () => { cancelled = true; };
    }, []);

    const sub = config?.subscription;
    const isActive = sub?.status === "active";
    const isExpired = sub?.status === "expired";

    const onPay = useCallback(async () => {
        if (!config?.configured) {
            toast.info("Payments aren't configured yet.");
            return;
        }
        setIsProcessing(true);
        try {
            await loadRazorpay();
            const { data: order } = await api.post("/payments/order");
            const rzp = new window.Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.order_id,
                name: "Happy Lab",
                description: `${order.plan_name} — ₹${order.price_inr}/year`,
                prefill: { email: user?.email, name: user?.full_name },
                theme: { color: "#ECA000" },
                handler: async (resp) => {
                    try {
                        await api.post("/payments/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        await refreshUser();
                        toast.success("Subscription activated! 🎉");
                        navigate("/");
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Payment verification failed");
                    } finally {
                        setIsProcessing(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.info("Payment cancelled.");
                        setIsProcessing(false);
                    },
                },
            });
            rzp.open();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Could not start checkout";
            toast.error(typeof detail === "string" ? detail : "Could not start checkout");
            setIsProcessing(false);
        }
    }, [config, user, refreshUser, navigate]);

    return (
        <Layout>
            <div className="mx-auto max-w-3xl space-y-8 animate-fade-in-up" data-testid="subscribe-page">
                <header className="space-y-2">
                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Sparkles className="h-3.5 w-3.5" />
                        Annual plan
                    </p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                        Subscribe to Happy Lab
                    </h1>
                    <p className="text-base text-muted-foreground">
                        Unlimited practice sessions, every avatar, every modality.
                    </p>
                </header>

                {loadError && (
                    <Card className="border-destructive/50 bg-destructive/5">
                        <CardContent className="p-4 text-sm text-destructive">{loadError}</CardContent>
                    </Card>
                )}

                {!config ? (
                    <Skeleton className="h-72 w-full rounded-2xl" />
                ) : (
                    <Card className="border-border/80 shadow-lg overflow-hidden" data-testid="subscribe-pricing-card">
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-7 border-b border-border/70">
                            {isActive ? (
                                <div className="space-y-2" data-testid="subscribe-active-state">
                                    <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700">
                                        <ShieldCheck className="h-3.5 w-3.5" /> You&apos;re subscribed
                                    </p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        Annual plan active
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Expires {sub.expires_at ? format(new Date(sub.expires_at), "d MMM yyyy") : "—"}
                                        {sub.days_remaining != null && <> · {sub.days_remaining} days remaining</>}
                                    </p>
                                </div>
                            ) : isExpired ? (
                                <div className="space-y-2" data-testid="subscribe-expired-state">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-rose-700">
                                        Subscription expired
                                    </p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        Renew for ₹{config.price_inr}/year
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Your previous plan ended on{" "}
                                        {sub.expires_at ? format(new Date(sub.expires_at), "d MMM yyyy") : "—"}.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-1.5" data-testid="subscribe-price-state">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        {config.plan_name}
                                    </p>
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-5xl font-semibold tracking-tight text-foreground tabular-nums" data-testid="subscribe-price">
                                            ₹{config.price_inr.toLocaleString()}
                                        </span>
                                        <span className="text-sm text-muted-foreground">/ year</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        One payment. {config.duration_days} days of unlimited access.
                                    </p>
                                </div>
                            )}
                        </div>

                        <CardContent className="p-6 space-y-5">
                            <ul className="space-y-2.5">
                                {FEATURES.map((f) => (
                                    <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
                                        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                                            <Check className="h-3 w-3" strokeWidth={3} />
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <div className="pt-2">
                                {config.configured ? (
                                    <Button
                                        size="lg"
                                        onClick={onPay}
                                        disabled={isProcessing}
                                        className="w-full bg-primary hover:bg-primary-hover font-semibold"
                                        data-testid="subscribe-pay-button"
                                    >
                                        <CreditCard className="h-4 w-4" />
                                        {isProcessing
                                            ? "Opening Razorpay…"
                                            : isExpired
                                                ? `Renew — ₹${config.price_inr}/year`
                                                : isActive
                                                    ? `Extend — ₹${config.price_inr}/year`
                                                    : `Pay with Razorpay — ₹${config.price_inr}/year`}
                                    </Button>
                                ) : (
                                    <div data-testid="subscribe-not-configured" className="space-y-2">
                                        <Button
                                            size="lg"
                                            disabled
                                            className="w-full"
                                            data-testid="subscribe-coming-soon-button"
                                        >
                                            <Lock className="h-4 w-4" />
                                            Payments coming soon
                                        </Button>
                                        <p className="text-xs text-muted-foreground text-center">
                                            Our team is finalising payment setup. Email{" "}
                                            <a href="mailto:connect@thehappylab.in" className="underline">
                                                connect@thehappylab.in
                                            </a>{" "}
                                            to be notified the moment we go live.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>

                        <div className="border-t border-border/70 bg-secondary/30 px-6 py-3 text-xs text-muted-foreground flex items-center justify-between gap-3">
                            <span className="inline-flex items-center gap-1.5">
                                <ShieldCheck className="h-3 w-3" />
                                Secured by Razorpay
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <Mail className="h-3 w-3" />
                                <a href="mailto:connect@thehappylab.in" className="hover:underline">connect@thehappylab.in</a>
                            </span>
                        </div>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
